// OWNER: FRONTEND-ADMIN. Pure report math for the Reports page (no DOM): totals, derived rates, CSV export.
import type { FunnelRow, HourBucket } from '../../../src/shared/api';
import { toCsv } from './csv';

export type GroupKey = 'week' | 'campaign' | 'user';

const ratio = (a: number, b: number): number | null => (b > 0 ? a / b : null);

/**
 * No-shows: CORE's rows carry `meetingsNoShow` (FunnelRowCore). For a row without it, recover them from held and
 * showRate (= held / (held + noShow)).
 * Exact when showRate > 0 or when nothing was held and nothing no-showed (showRate null). Returns null when it
 * can't be known (showRate 0: at least one no-show, count unknown).
 */
export function noShows(r: Pick<FunnelRow, 'meetingsHeld' | 'showRate'> & { meetingsNoShow?: number }): number | null {
  // CORE's funnel rows carry the exact count (FunnelRowCore.meetingsNoShow); older shapes fall back to the recovery.
  if (typeof r.meetingsNoShow === 'number' && Number.isFinite(r.meetingsNoShow)) return r.meetingsNoShow;
  if (r.showRate === null) return 0;
  if (r.showRate > 0) return Math.round(r.meetingsHeld / r.showRate - r.meetingsHeld);
  return null;
}

/** Sum of every count; rates recomputed from the sums (never averaged). */
export function totalsRow(rows: FunnelRow[]): FunnelRow {
  const sum = (k: keyof FunnelRow) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
  const dials = sum('dials');
  const connects = sum('connects');
  const dm = sum('dmConversations');
  const booked = sum('meetingsBooked');
  const held = sum('meetingsHeld');
  const ns = rows.map(noShows);
  const noShowTotal = ns.some((x) => x === null) ? null : (ns as number[]).reduce((a, b) => a + b, 0);
  return {
    key: 'total', week: '', campaignId: null, campaignName: null, userId: null, userName: null,
    dials, connects, dmConversations: dm, meetingsBooked: booked, meetingsHeld: held,
    showRate: noShowTotal === null ? null : ratio(held, held + noShowTotal),
    dialsPerMeeting: booked > 0 ? dials / booked : null,
    connectRate: ratio(connects, dials),
    setRate: ratio(booked, dm),
    vmLive: sum('vmLive'), vmDropped: sum('vmDropped'),
    talkMinutes: Math.round(sum('talkMinutes') * 10) / 10,
  };
}

/** True when the total show rate could not be computed exactly (shown as "not measurable"). */
export function totalShowRateUnknown(rows: FunnelRow[]): boolean {
  return rows.some((r) => noShows(r) === null);
}

const pctText = (x: number | null) => (x === null ? '' : (x * 100).toFixed(1));

/** CSV of the funnel table (+ a TOTAL row when there is more than one row). The first column is the grouping
 *  label ('week', 'campaign', 'setter' in that order, whichever are selected) or 'range' when ungrouped. */
export function funnelCsv(rows: FunnelRow[], groupBy: GroupKey[], from: string, to: string): string {
  const groups: Array<{ head: string; get: (r: FunnelRow) => string }> = [];
  if (groupBy.includes('week')) groups.push({ head: 'week', get: (r) => r.week });
  if (groupBy.includes('campaign')) groups.push({ head: 'campaign', get: (r) => r.campaignName ?? '' });
  if (groupBy.includes('user')) groups.push({ head: 'setter', get: (r) => r.userName ?? '' });
  if (groups.length === 0) groups.push({ head: 'range', get: () => `${from} to ${to}` });
  const head = [...groups.map((g) => g.head), 'dials', 'connects', 'connect_rate_pct', 'dm_conversations', 'set_rate_pct',
    'meetings_booked', 'meetings_held', 'show_rate_pct', 'dials_per_meeting', 'vm_live', 'vm_dropped', 'talk_minutes', 'from', 'to'];
  const metrics = (r: FunnelRow): Array<string | number> => [
    r.dials, r.connects, pctText(r.connectRate), r.dmConversations, pctText(r.setRate), r.meetingsBooked, r.meetingsHeld,
    pctText(r.showRate), r.dialsPerMeeting === null ? '' : r.dialsPerMeeting.toFixed(1), r.vmLive, r.vmDropped, r.talkMinutes, from, to,
  ];
  const out: Array<Array<string | number>> = [head];
  for (const r of rows) out.push([...groups.map((g) => g.get(r)), ...metrics(r)]);
  if (rows.length > 1) out.push(['TOTAL', ...groups.slice(1).map(() => ''), ...metrics(totalsRow(rows))]);
  // campaign and setter names are typed by people; guard them before a spreadsheet opens the file
  return toCsv(out, { guardFormulas: true });
}

/** Hour buckets for the chart: fills every hour 8..20 (prospect-local calling hours) plus any hour with data. */
export function hourSeries(buckets: HourBucket[]): Array<HourBucket & { rate: number | null }> {
  const by = new Map(buckets.map((b) => [b.localHour, b]));
  const hours = new Set<number>([...Array.from({ length: 13 }, (_, i) => 8 + i), ...buckets.map((b) => b.localHour)]);
  return [...hours].sort((a, b) => a - b).map((h) => {
    const b = by.get(h) ?? { localHour: h, dials: 0, connects: 0, meetings: 0 };
    return { ...b, rate: ratio(b.connects, b.dials) };
  });
}

export function hourLabel(h: number): string {
  const ampm = h < 12 ? 'am' : 'pm';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}${ampm}`;
}
